import { type InputRef, Table, type TableProps } from 'antd';
import { pick } from 'lodash/fp';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type DealerListDataFragment } from '../../../api/fragments/DealerListData';
import { useListDealersQuery } from '../../../api/queries/listDealers';
import { DealerSortingField } from '../../../api/types';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import { useMultipleDealerIds } from '../../../components/contexts/DealerContextManager';
import PaginatedTableWithContext from '../../../components/PaginatedTableWithContext';
import InternalErrorResult from '../../../components/results/InternalErrorResult';
import SearchBox from '../../../components/SearchBox';
import makeGetSortingRule from '../../../utilities/makeGetSortingRule';
import renderFilterIcon from '../../../utilities/renderFilterIcon';
import useSortAndFilterCache, { SortAndFilterCacheKey } from '../../../utilities/useSortAndFilterCache';
import useListReducer from './useListReducer';

const getSortField = makeGetSortingRule((field): DealerSortingField => {
    switch (field) {
        case 'displayName':
            return DealerSortingField.DisplayName;

        case 'userIds':
            return DealerSortingField.UserCount;

        default:
            throw new Error('Unknown Sorting Field!');
    }
});

const DealerList = () => {
    const { t } = useTranslation(['dealerList']);
    const inputRef = useRef<InputRef>(null);

    // get company context
    const company = useCompany(true);

    const navigate = useNavigate();

    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.Dealer);

    // get state from a reducer
    const [state, dispatch] = useListReducer(currentCache);

    const { page, pageSize, sort, filter } = state;

    // filter by the dealer selected
    const { dealerIds } = useMultipleDealerIds();
    const { data, loading, error } = useListDealersQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: pick(['field', 'order'], sort),
            filter: { ...filter, companyId: company?.id, id: dealerIds?.length === 1 ? dealerIds[0] : null },
        },
    });

    const dataSource = useMemo(() => (data?.page?.items || []).map(item => ({ ...item, key: item.id })), [data]);
    const total = data?.page?.count || 0;

    const onChange = useCallback<TableProps<DealerListDataFragment>['onChange']>(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort':
                    return dispatch({
                        type: 'setSort',
                        sortBy: getSortField(sorter),
                    });

                case 'filter':
                    return dispatch({
                        type: 'setFilter',
                        filterBy: {
                            displayName: !filters.displayName ? null : (filters.displayName[0] as string),
                        },
                    });

                default:
                    return undefined;
            }
        },
        [dispatch]
    );

    const showFilterDropDown = useCallback(
        (props, dataIndex) =>
            SearchBox({
                filterDropDownProps: props,
                dataIndex,
                onRef: node => {
                    inputRef.current = node;
                },
            }),
        [inputRef]
    );

    const onFilterDropdownVisibleChange = useCallback(
        visible => {
            if (visible) {
                setTimeout(() => inputRef.current.select(), 100);
            }
        },
        [inputRef]
    );

    if (!loading) {
        if (error) {
            return <InternalErrorResult />;
        }
    }

    return (
        <PaginatedTableWithContext
            company={company}
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={(row: DealerListDataFragment) => ({
                onClick: () => {
                    setCache(state);
                    navigate(`/admin/dealers/${row.id}`);
                },
                style: { cursor: 'pointer' },
            })}
            rowKey="id"
            state={state}
            tableName={t('dealerList:title')}
            total={total}
        >
            {!company && (
                <Table.Column
                    key="company"
                    dataIndex={['company', 'displayName']}
                    title={t('dealerList:columns.company')}
                />
            )}
            <Table.Column
                key="displayName"
                dataIndex="displayName"
                filterDropdown={props => showFilterDropDown(props, 'displayName')}
                filterIcon={renderFilterIcon}
                filteredValue={filter.displayName ? [filter.displayName] : undefined}
                onFilterDropdownOpenChange={onFilterDropdownVisibleChange}
                sortOrder={sort.field === DealerSortingField.DisplayName ? sort.orderValue : undefined}
                title={t('dealerList:columns.displayName')}
                sorter
            />
            <Table.Column
                key="userIds"
                dataIndex="userIds"
                render={userIds => userIds.length}
                sortOrder={sort.field === DealerSortingField.UserCount ? sort.orderValue : undefined}
                title={t('dealerList:columns.users')}
                sorter
            />
        </PaginatedTableWithContext>
    );
};

export default DealerList;
